import {
  Controller,
  FieldError,
  FieldValues,
  Path,
  useFormContext,
} from "react-hook-form";
import useTranslations from "../../core/i18n/useTranslations";
import { Column, Row, Spacer, TwoColGrid } from "../Layout/Layout";
import TextInput from "../../ui-lib/components/Inputs/TextInput";
import { validateHexadecimal } from "../../core/helpers/validation";
import { Radio } from "../../ui-lib/components/Radio/Radio";
import { AntennaDirection } from "../../core/api/observationIdentifiers/types";
import { get } from "lodash";
import { Button } from "../../ui-lib/components/Button/Button";
import styles from "./ObservationIdentifiers.module.css";
import inputStyles from "../../ui-lib/components/Inputs/Input.module.css";
import { Icon } from "../../ui-lib/components/Icon";

const buildPath = (base: string, index?: number, field?: string) => {
  if (!field) return index === undefined ? base : `${base}.${index}`;

  if (index !== undefined) return `${base}.${index}.${field}`;
  if (base) return `${base}.${field}`;
  return field;
};

type ObservationIdentifierRowProps = {
  base?: string;
  index?: number;
  required?: boolean;
  showDeleteButton?: boolean;
  onDelete?: () => void;
  hasLocation?: boolean;
};

const ObservationIdentifierRow = <TFormValues extends FieldValues>({
  base = "",
  index,
  required = true,
  showDeleteButton = false,
  onDelete,
  hasLocation = true,
}: ObservationIdentifierRowProps) => {
  const t = useTranslations();
  const {
    control,
    trigger,
    formState: { errors },
    getValues,
    clearErrors,
  } = useFormContext<TFormValues>();

  const macAddressPath = buildPath(base, index, "macAddress");
  const antennaDirectionPath = buildPath(base, index, "antennaDirection");
  const observationIdentifierPath = buildPath(
    base,
    index,
    "observationIdentifier",
  );
  const globalPath = base ? "dataDefinitions" : "macAddress";

  const validateFormat = (value: string) => {
    if (!value) return true;

    return validateHexadecimal(12, 12, value);
  };

  const validateUniqueness = (value: string) => {
    if (!value || !base) return true;

    const all = getValues(base as Path<TFormValues>).map(
      (item: any) => item.macAddress,
    );
    return all.filter((v: any) => v === value).length <= 1;
  };

  const errorMessages = (type: string) =>
    ({
      required: t("Errors:input_field_required"),
      validateFormat: t("Locations:oi_validation_mac_address"),
      validateUniqueness: t("Locations:oi_validation_mac_address_unique"),
    })[type];

  const rowError =
    get(errors, observationIdentifierPath)?.type === "validateGlobalUniqueness";

  return (
    <Column
      align="start"
      className={rowError ? styles.containerError : styles.container}
    >
      {rowError && (
        <>
          <div
            data-cy="validation_error"
            className={inputStyles.validationError}
          >
            <Icon name="exclamation-circle" size={14} />
            {t("Locations:oi_validation_observation_identifier_unique")}
          </div>
          <Spacer size={8} />
        </>
      )}
      <Row align="center" type="fill" className="w-100">
        <TwoColGrid columnGap={32}>
          <Controller
            name={macAddressPath as Path<TFormValues>}
            control={control}
            rules={{
              required: required,
              validate: {
                validateFormat,
                validateUniqueness,
              },
            }}
            render={({ field }) => {
              const error = get(errors, macAddressPath) as FieldError;
              return (
                <TextInput
                  required={required}
                  label={t("Locations:oi_labels_mac_address")}
                  placeholder={t("Locations:oi_placeholder_mac_address")}
                  value={field.value}
                  disabled={!hasLocation}
                  onChange={(value) => {
                    field.onChange(value);
                    clearErrors(observationIdentifierPath as Path<TFormValues>);
                    trigger(globalPath as Path<TFormValues>);
                  }}
                  validationError={error && errorMessages(error.type)}
                />
              );
            }}
          />
          <Row align="center" type="left" className="h-100">
            <Controller
              name={antennaDirectionPath as Path<TFormValues>}
              control={control}
              render={({ field }) => (
                <fieldset>
                  <legend>{t("Locations:oi_labels_antenna_direction")}</legend>
                  <div className={styles.radioGroup}>
                    <Radio
                      name={antennaDirectionPath}
                      inputId={`${antennaDirectionPath}In`}
                      text={t("Locations:oi_labels_antenna_in")}
                      value={AntennaDirection.AntIn}
                      checked={field.value === AntennaDirection.AntIn}
                      onChange={() => {
                        field.onChange(AntennaDirection.AntIn);
                        clearErrors(
                          observationIdentifierPath as Path<TFormValues>,
                        );
                      }}
                    />
                    <Spacer size={24} />
                    <Radio
                      name={antennaDirectionPath}
                      inputId={`${antennaDirectionPath}Out`}
                      text={t("Locations:oi_labels_antenna_out")}
                      value={AntennaDirection.AntOut}
                      checked={field.value === AntennaDirection.AntOut}
                      onChange={() => {
                        field.onChange(AntennaDirection.AntOut);
                        clearErrors(
                          observationIdentifierPath as Path<TFormValues>,
                        );
                      }}
                    />
                  </div>
                </fieldset>
              )}
            />
          </Row>
        </TwoColGrid>
        <Spacer size={32} />
        {showDeleteButton && (
          <Button
            image="x"
            variant="secondary"
            onClick={onDelete}
            className={styles.removeButton}
          />
        )}
      </Row>
    </Column>
  );
};

export default ObservationIdentifierRow;
